import type {
  TextMessageResponseDto,
  AttachmentMessageResponseDto,
  QuoteMessageResponseDto,
  ContractMessageResponseDto,
  SystemMessageResponseDto,
  PaymentPendingMessageResponseDto,
  CancellationRequestMessageResponseDto,
} from "@semo/api-contracts";

export interface LocalMessageState {
  isPending?: boolean;
  tempId?: string;
  isError?: boolean;
  retryPayload?: any;
}

/**
 * Extended ChatMessageResponseDto with optimistic update support
 *
 * **Pattern**: Union of intersections preserves discriminated union narrowing
 * - Can't intersect union types directly with TypeScript
 * - Must apply intersection to each union member separately
 * - Enables proper type narrowing: `if (msg.messageType === 'quote') { msg.quoteData }`
 */
export type PendingChatMessage =
  | (TextMessageResponseDto & LocalMessageState)
  | (AttachmentMessageResponseDto & LocalMessageState)
  | (QuoteMessageResponseDto & LocalMessageState)
  | (ContractMessageResponseDto & LocalMessageState)
  | (SystemMessageResponseDto & LocalMessageState)
  | (PaymentPendingMessageResponseDto & LocalMessageState)
  | (CancellationRequestMessageResponseDto & LocalMessageState);

export type PendingQuoteMessage = QuoteMessageResponseDto & LocalMessageState;

/**
 * Type guard to check if a message is pending
 */
export function isPendingMessage(
  message: PendingChatMessage,
): message is PendingChatMessage & { isPending: true } {
  return message.isPending === true;
}

/**
 * Generate a temporary ID for optimistic updates
 */
export function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
